import { useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import { getServingLabel, mergeFoodLogs } from "../../../../utils/daily-stats";
import { getDisplayDate } from "../../../../utils/date-helpers";

const FoodLog = ({ stats, isPeriod, isCalendar = false }) => {
  const hasFoodLogData = Boolean(stats.foodLog || stats.internalFoodLog);

  if (!hasFoodLogData) {
    return (
      <NoDataState stats={stats} isPeriod={isPeriod} isCalendar={isCalendar} />
    );
  }

  if (isPeriod) return <PeriodFoodTables stats={stats} />;

  const source =
    stats.foodLog && stats.internalFoodLog
      ? `${stats.foodLog.source}, ${stats.internalFoodLog.source}`
      : stats.foodLog?.source || stats.internalFoodLog?.source || "";

  const mergedFoodLog = stats?.internalFoodLog
    ? mergeFoodLogs(stats.foodLog, stats.internalFoodLog, source)
    : stats?.foodLog;

  const mergedStats = {
    ...stats,
    foodLog: mergedFoodLog,
    source,
  };

  if (
    source.includes("macrosFirst") ||
    source === "mealPlanner" ||
    source.includes("myfitnesspal")
  )
    return <MacrosFirstTable stats={mergedStats} isCalendar={isCalendar} />;
  else if (source.includes("cronometer"))
    return <CronometerTable stats={mergedStats} isCalendar={isCalendar} />;
  else
    return (
      <NoDataState stats={stats} isPeriod={isPeriod} isCalendar={isCalendar} />
    );
};

const NoDataState = ({ stats, isPeriod, isCalendar }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div className="nutrition-summary-head">
      <div className="summary-title">
        {isPeriod ? (
          <span>
            {getDisplayDate(stats.startOfWeek)} to{" "}
            {getDisplayDate(stats.endOfWeek)}
          </span>
        ) : (
          !isCalendar && <span>{getDisplayDate(stats.utcDate, true)}</span>
        )}
      </div>
      <div className="empty-state">
        <span className="font-bold">{t("foodLog.noFoodData")}</span>
      </div>
    </div>
  );
};

const DisplayDate = ({ date, source, isCalendar = false }) => {
  const { t } = useTranslation("athlete-portal");
  const sourceString = (source || "")
    .split(",")
    .map((s) => {
      const trimmed = s.trim();
      if (trimmed === "cronometer") return "Cronometer";
      if (trimmed === "macrosFirst") return "MacrosFirst";
      if (trimmed === "mealPlanner") return "Meal Planner";
      if (trimmed === "myfitnesspal") return "MyFitnessPal";
      return trimmed;
    })
    .join(", ");
  return (
    <table className="report-table">
      <thead>
        <tr>
          <th>
            {!isCalendar && (
              <p className="font-bold">{getDisplayDate(date, true)}</p>
            )}
            <div className="chip info max-w-[8.65rem]">
              {t("foodLog.syncedVia", { source: sourceString })}
            </div>
          </th>

          <th>
            <p className="protein">{t("foodLog.protein")}</p>
            <p className="st1">(g)</p>
          </th>
          <th>
            <p className="carbs">{t("foodLog.carbs")}</p>
            <p className="st1">(g)</p>
          </th>
          <th>
            <p className="fat">{t("foodLog.fat")}</p>
            <p className="st1">(g)</p>
          </th>
          <th>
            <p className="font-bold">{t("foodLog.fiber")}</p>
            <p className="st1">(g)</p>
          </th>
          <th>
            <p className="font-bold">{t("foodLog.kcal")}</p>
          </th>
        </tr>
      </thead>
    </table>
  );
};

const PeriodFoodTables = ({ stats }) => {
  const { t } = useTranslation("athlete-portal");
  const { foodLog } = stats;
  //Group by date
  const logsByDate = foodLog.reduce((acc, entry) => {
    const { date } = entry;
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry.foodLog);
    return acc;
  }, {});

  //Fusion by date

  const mergedLogs = Object.entries(logsByDate).map(([date, logs]) => {
    const mainLog = logs.find((l) => l.source !== "mealPlanner");
    const internalLog = logs.find((l) => l.source === "mealPlanner");

    let mergedLog;

    if (mainLog && internalLog) {
      const sourceMainLog = mainLog?.source;
      mergedLog = mergeFoodLogs(mainLog, internalLog, sourceMainLog);
      mergedLog.source = `${mainLog.source}, ${internalLog.source}`;
    } else {
      mergedLog = mainLog || internalLog;
    }

    return {
      date: Number(date),
      foodLog: mergedLog,
    };
  });

  const sortedLog = [...mergedLogs].sort((a, b) => {
    return b.date - a.date;
  });

  return sortedLog.map((log, index) => {
    const { date, foodLog } = log;
    const { source } = foodLog;

    const foodStats = { foodLog, utcDate: date };
    if (
      source.includes("macrosFirst") ||
      source === "mealPlanner" ||
      source.includes("myfitnesspal")
    )
      return (
        <MacrosFirstTable
          stats={foodStats}
          key={`macrosfirst-${index}-table`}
        />
      );
    else if (source.includes("cronometer"))
      return (
        <CronometerTable stats={foodStats} key={`cronometer-${index}-table`} />
      );
    else
      return (
        <div className="empty-state" key={`no-data-${index}`}>
          <span>{getDisplayDate(date, true)}</span>
          <span className="font-bold">{t("foodLog.noFoodData")}</span>
        </div>
      );
  });
};

// MacrosFirst always has at least 4 objects in the meals array
// Even when there are no food in the object
// But the meals inside the food object can be empty
// It also includes the following properties on an instance of the foods array (eg, an egg)
// name: "Egg";
// servingName: "large egg";
// servingSize: 3;
// fats:  15;
// proteins: 18;
// kcal: 210;

const macrosFirstOrder = ["Breakfast", "Lunch", "Dinner", "Snack"];

const MacrosFirstTable = ({ stats, isCalendar }) => {
  const meals = stats.foodLog?.meals || stats.internalFoodLog?.meals || [];

  const [expandedMeals, setExpandedMeals] = useState(meals.map(() => true));

  const toggleMeal = (index) => {
    setExpandedMeals((prev) =>
      prev.map((val, i) => (i === index ? !val : val))
    );
  };

  const orderedMeals = meals.sort((a, b) => {
    const indexA = macrosFirstOrder.indexOf(a.name);
    const indexB = macrosFirstOrder.indexOf(b.name);
    return (
      (indexA === -1 ? macrosFirstOrder.length : indexA) -
      (indexB === -1 ? macrosFirstOrder.length : indexB)
    );
  });
  return (
    <article className="foodlog-container">
      <DisplayDate
        date={stats.utcDate}
        source={stats.source || stats.foodLog?.source || "macrosFirst"}
        isCalendar={isCalendar}
      />
      {orderedMeals.map((meal, index) => {
        const calories = Math.round(
          (meal.proteins || 0) * 4 +
            (meal.carbs || 0) * 4 +
            (meal.fats || 0) * 9
        );
        return (
          <section
            role="table-wrapper"
            key={`macrosfirst-${stats.utcDate}-${meal.name}-${index}`}
          >
            <table className="report-table">
              <thead onClick={() => toggleMeal(index)}>
                <tr>
                  <th>
                    <SvgIcon
                      src={"/img/new-icons/arrow-square-right.svg"}
                      classNames={expandedMeals[index] ? "rotated" : ""}
                    />{" "}
                    {meal.name}
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.proteins)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.carbs)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.fats)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.fibers)}</span>
                  </th>
                  <th>
                    <span className="clipped">{calories}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {expandedMeals[index] &&
                  meal.foods.map((food, foodIndex) => {
                    const calories = Math.round(
                      (food.proteins || 0) * 4 +
                        (food.carbs || 0) * 4 +
                        (food.fats || 0) * 9
                    );
                    return (
                      <tr
                        key={`macrosfirst-${stats.utcDate}-${food.name}-${foodIndex}`}
                      >
                        <td>
                          <div>
                            <p>{food.name}</p>
                            <p className="p5">{getServingLabel(food)}</p>
                          </div>
                        </td>
                        <td>{Math.round(food.proteins || 0)}</td>
                        <td>{Math.round(food.carbs || 0)}</td>
                        <td>{Math.round(food.fats || 0)}</td>
                        <td>{Math.round(food.fibers || 0)}</td>
                        <td>{calories}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </section>
        );
      })}
    </article>
  );
};

// Cronometer only stores the registered meals in the meals array
// Meaning, it will be only populated if the user has registered a meal
// So possibly it will skip meals if the user has, so if you are reading this in the future, it's not a bug, it's a feature
// It includes the following properties on an instance of the food array (eg, an omelet)
// name: "Omelet, Plain";
// servingName: "1.0 - large egg used - 1 whole egg or 2 egg whites";
const cronometerOrder = ["Breakfast", "Lunch", "Dinner", "Snack"];

const CronometerTable = ({ stats, isCalendar }) => {
  const { meals } = stats.foodLog;

  const [expandedMeals, setExpandedMeals] = useState(meals.map(() => true));

  const toggleMeal = (index) => {
    setExpandedMeals((prev) =>
      prev.map((val, i) => (i === index ? !val : val))
    );
  };

  const orderedMeals = meals.sort((a, b) => {
    const indexA = cronometerOrder.indexOf(a.name);
    const indexB = cronometerOrder.indexOf(b.name);
    // If the name is in the order array, sort according to its index.
    // Otherwise, push it to the end (return a large index).
    return (
      (indexA === -1 ? cronometerOrder.length : indexA) -
      (indexB === -1 ? cronometerOrder.length : indexB)
    );
  });
  return (
    <article className="foodlog-container">
      <DisplayDate
        date={stats.utcDate}
        source={stats.source || stats.foodLog?.source || "cronometer"}
        isCalendar={isCalendar}
      />
      {orderedMeals.map((meal, index) => {
        const calories = Math.round(
          (meal.proteins || 0) * 4 +
            (meal.carbs || 0) * 4 +
            (meal.fats || 0) * 9
        );
        return (
          <section
            role="table-wrapper"
            key={`cronometer-${stats.utcDate}-${meal.name}-${index}`}
          >
            <table className="report-table">
              <thead onClick={() => toggleMeal(index)}>
                <tr>
                  <th>
                    <SvgIcon
                      src={"/img/new-icons/arrow-square-right.svg"}
                      classNames={expandedMeals[index] ? "rotated" : ""}
                    />{" "}
                    {meal.name}
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.proteins)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.carbs)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.fats)}</span>
                  </th>
                  <th>
                    <span className="clipped">{Math.round(meal.fibers)}</span>
                  </th>
                  <th>
                    <span className="clipped">{calories}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {expandedMeals[index] &&
                  meal.foods.map((food, foodIndex) => (
                    <tr
                      key={`cronometer-${stats.utcDate}-${food.name}-${foodIndex}`}
                    >
                      <td>{food.name}</td>
                      <td colSpan={5} className="serving">
                        <p className="p5">{food.servingName}</p>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </article>
  );
};

export default FoodLog;
